/* eslint-disable no-console */
import { Theme } from "@animech/ui";
import { createElement } from "react";
import { render } from "react-dom";
import { joinPaths } from "@animech/axiom-utility";
import {
	ModelParser,
	AttacherNodesByModelId,
	ColliderNodesByModelId,
	MorphNodesByModelId,
} from "@animech/supermodel";
import {
	Application,
	QuerySettingsError,
	SettingsFromQuery,
} from "./application";
import { ViewerApplication } from "./viewer-application/viewer-application";
import { viewerPath } from "./viewer-application";
import { grythyttanSetupProductFromArticleNumber } from "./viewer-application/grythyttan-setup-product";
import {
	ARTICLE_PARAM,
	AUTO_CAMERA_FOCUS_PARAM,
	COUNTRY_CODE_PARAM,
	DEFAULT_GUID,
	DESIGN_TEMPLATE_PARAM,
	GUID_PARAM,
	LAN_PARAM,
	PRICELIST_ID_PARAM,
	UNIT_SYSTEM_PARAM,
	VAT_PARAM,
} from "./query-params";
import { CriticalErrorWrapper, BootstrappingApp, theme } from "src/ui";
import { BuildInfo, httpGet } from "src/utility";
import { ModelData } from "src/common";
import { Locale } from "@definitions";
import { locales } from "src/definitions/locale-definitions";
import { UnitSystem, unitSystems } from "src/core";

type AppConfig = {
	baseUrl: string;
	useLocalStorage: boolean;
	apiUrl: string;
	apiKey: string;
	arEnabled: boolean;
	arApiUrl: string;
	arUsername: string;
	arPassword: string;
	arIsProduction: boolean;
};

const config = {
	domRoot: "app-root",
	configUrl: "appconfig.json",
	studioProjectPath: "studioproject.json",
	attacherDataPath: "resources/attacherdata.json",
	colliderDataPath: "resources/colliderdata.json",
	morphDataPath: "resources/morphdata.json",
	minLoadingTime: undefined,
};

export class Bootstrapper {
	private _application?: Application;
	private _viewerApplication?: ViewerApplication;

	public async start(): Promise<void> {
		window.addEventListener("unhandledrejection", (event) => {
			console.error("Unhandled Promise Rejection:", {
				reason: event.reason,
				stack: event.reason?.stack,
				promise: event.promise,
			});
		});

		if (BuildInfo.mode !== "production") {
			console.log(
				`Running version ${BuildInfo.version} in ${BuildInfo.mode} mode`,
			);
		}

		if (window.electron) {
			console.log(`Running app in Electron`, window.electron);
		}

		const startTime = new Date();

		this._renderLoading(theme);

		const {
			baseUrl,
			apiUrl,
			arEnabled,
			arUsername,
			arPassword,
			arIsProduction,
			useLocalStorage,
		} = await this._getAppConfig();
		const resourceBaseUrl = await this._getResourceBaseUrl(baseUrl, apiUrl);

		const remainingMinLoadingTime =
			config.minLoadingTime !== undefined
				? this._getRemainingMinLoadingTime(startTime, config.minLoadingTime)
				: undefined;
		if (arEnabled && !(arUsername && arPassword)) {
			throw new Error(
				`Username and password must be supplied when AR is enabled`,
			);
		}
		const arConfig = arEnabled
			? {
					serverUsername: arUsername,
					serverPassword: arPassword,
					isProduction: arIsProduction,
					exportModel: undefined,
				}
			: undefined;

		const urlPath = window.location.hash.split("?")[0].replace(/^#\/?/, "");
		const isViewerMode = urlPath === viewerPath;

		if (isViewerMode) {
			const queryString = window.location.hash.split("?")[1];
			const urlParams = new URLSearchParams(queryString);
			const articleNumber = urlParams.get(ARTICLE_PARAM) ?? null;
			const designTemplateId = urlParams.get(DESIGN_TEMPLATE_PARAM) ?? null;
			const guidFromParam = urlParams.get(GUID_PARAM) ?? null;
			const guid =
				guidFromParam ?? (!this._isAppInIframe() ? DEFAULT_GUID : null);
			try {
				this._viewerApplication = new ViewerApplication({
					domRoot: config.domRoot,
					resourceBaseUrl,
					theme,
					articleNumber,
					designTemplateId,
					guid,
					arConfig,
					setupProductFromArticleNumber:
						grythyttanSetupProductFromArticleNumber,
				});
			} catch (error) {
				console.error("Failed creating viewer application, error: ", error);
			}
			return;
		}

		const settingsFromQuery = this._getSettingsFromQuery();

		if ("errorMessage" in settingsFromQuery) {
			this._renderCriticalError(theme, settingsFromQuery.errorMessage);
			return;
		}

		try {
			const modelData: ModelData = {
				attacherNodesByModel: await this._getAttacherNodesByModelId(),
				colliderNodesByModel: await this._getColliderNodesByModelId(),
				morphNodesByModel: await this._getMorphNodesByModelId(),
			};

			this._application = new Application({
				domRoot: config.domRoot,
				resourceBaseUrl,
				minLoadingTime: remainingMinLoadingTime,
				useLocalStorage,
				arConfig,
				theme,
				modelData,
				settingsFromQuery,
				autoCameraFocusEnabled: this._getAutoCameraFocusFromQuery(),
			});
		} catch (error) {
			console.error("Failed creating application, error: ", error);
		}
	}

	private _getRemainingMinLoadingTime(startTime: Date, minLoadingTime: number) {
		const timeLeft =
			minLoadingTime - (new Date().getTime() - startTime.getTime());
		return timeLeft > 0 ? timeLeft : 0;
	}

	private async _getResourceBaseUrl(
		baseUrl: string,
		apiUrl: string,
	): Promise<string> {
		return apiUrl
			? joinPaths(apiUrl, "resources/published/file:/")
			: joinPaths(baseUrl, "resources/");
	}

	private _renderCriticalError(theme: Theme, error: string): void {
		render(
			createElement(CriticalErrorWrapper, { theme, error }),
			document.getElementById(config.domRoot),
		);
	}

	private _renderLoading(theme: Theme): void {
		render(
			createElement(BootstrappingApp, { theme }),
			document.getElementById(config.domRoot),
		);
	}

	private async _getAppConfig(): Promise<AppConfig> {
		const useLocalStorage = process.env.USE_LOCAL_STORAGE === "true";
		const arEnabled = process.env.AR_ENABLED === "true";
		const arIsProduction = process.env.AR_IS_PRODUCTION === "true";
		const appConfig = JSON.parse(await httpGet(config.configUrl));
		return {
			...appConfig,
			useLocalStorage,
			arEnabled,
			arIsProduction,
			arUsername: process.env.AR_USERNAME,
			arPassword: process.env.AR_PASSWORD,
		};
	}

	private async _getAttacherNodesByModelId(): Promise<AttacherNodesByModelId> {
		const serializedAttacherDatas = await httpGet(config.attacherDataPath);
		const deserializedAttacherNodes =
			ModelParser.deserializeAttacherNodesByModelId(serializedAttacherDatas);

		return deserializedAttacherNodes;
	}

	private async _getColliderNodesByModelId(): Promise<ColliderNodesByModelId> {
		const serializedColliderDatas = await httpGet(config.colliderDataPath);
		const deserializedColliderNodes =
			ModelParser.deserializeColliderNodesByModelId(serializedColliderDatas);

		return deserializedColliderNodes;
	}

	private async _getMorphNodesByModelId(): Promise<MorphNodesByModelId> {
		const serializedMorphDatas = await httpGet(config.morphDataPath);
		const deserializedMorphNodes =
			ModelParser.deserializeMorphNodesByModelId(serializedMorphDatas);

		return deserializedMorphNodes;
	}

	private _getSettingsFromQuery(): SettingsFromQuery | QuerySettingsError {
		const defaultSettings: SettingsFromQuery = {
			// We need guid hardcoded to be able to test API without adding a query every page refresh.
			guid: DEFAULT_GUID,
			pricelistid: null,
			vat: null,
			lan: "en",
			unitsystem: "metric",
			countrycode: null,
			designtemplate: null,
		};

		const queryError: Partial<QuerySettingsError> = {};
		const queryString = window.location.hash.split("?")[1];
		if (!queryString && this._isAppInIframe()) {
			return { errorMessage: "Missing query string" };
		}

		const urlParams = new URLSearchParams(queryString);

		const settings: Partial<SettingsFromQuery> = {};

		const settingsParams: (keyof SettingsFromQuery)[] = [
			LAN_PARAM,
			GUID_PARAM,
			PRICELIST_ID_PARAM,
			VAT_PARAM,
			COUNTRY_CODE_PARAM,
			DESIGN_TEMPLATE_PARAM,
			UNIT_SYSTEM_PARAM,
		];

		for (const settingsParam of settingsParams) {
			const param = urlParams.get(settingsParam);

			if (settingsParam === LAN_PARAM) {
				const isLanguageValid = locales.includes(param as Locale);

				if (!isLanguageValid) {
					console.warn(
						`Missing or incorrect lan in query string ( ${param} ). Available languages are: [${locales.join(",")}]`,
					);
					settings[settingsParam] = defaultSettings.lan;
					continue;
				}
				settings[settingsParam] = param as Locale;
			} else if (settingsParam === GUID_PARAM) {
				if (!param) {
					queryError.errorMessage = `Missing or incorrect guid in query string ( ${param} )`;
					continue;
				}
				settings[settingsParam] = param;
			} else if (settingsParam === UNIT_SYSTEM_PARAM) {
				const isUnitSystemValid =
					param && unitSystems.includes(param as UnitSystem);

				if (!isUnitSystemValid) {
					settings[settingsParam] = defaultSettings.unitsystem;
					continue;
				}
				settings[settingsParam] = param as UnitSystem;
			} else {
				settings[settingsParam] = param;
			}
		}

		if (!this._isAppInIframe()) {
			settings.guid = settings.guid ?? defaultSettings.guid;
			settings.lan = settings.lan ?? defaultSettings.lan;
		}

		if ("errorMessage" in queryError && this._isAppInIframe()) {
			return queryError as QuerySettingsError;
		}

		return settings as SettingsFromQuery;
	}

	private _getAutoCameraFocusFromQuery(): boolean {
		const queryString = window.location.hash.split("?")[1];
		const param = new URLSearchParams(queryString).get(AUTO_CAMERA_FOCUS_PARAM);
		return !(param === "false" || param === "0");
	}

	private _isAppInIframe() {
		try {
			return window.self !== window.top;
			// If we're unable to detect wether we're in an iframe we'll default to assume that it is.
		} catch (e) {
			return true;
		}
	}
}
